import { useMemo, useState, type ReactNode } from "react";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import {
  Save,
  FileText,
  PenLine,
  UserPlus,
  Mail,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Maximize2,
  Download,
  FileImage,
  Upload,
  Sparkles,
  ShieldCheck,
  AlertTriangle,
  Flame,
  Scale,
  MapPin,
  Building2,
  CalendarDays,
  Handshake,
  Activity,
  ClipboardCheck,
  Phone,
  ArchiveRestore,
  PhoneCall,
  ClipboardList,
  User,
  Hash,
  StickyNote,
  ArrowRight,
  Copy,
  Send,
  Clock,
  BadgeCheck,
  Info,
  ExternalLink,
  Landmark,
  Receipt,
  Truck,
  Bell,
  ChevronLeft,
  ChevronRight,
  Circle,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { SiteHeader } from "@/components/site-header";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { AuthGate } from "@/components/auth-gate";
import { ReadinessPanel } from "@/components/workspace/readiness-panel";
import { useCase, type Case } from "@/lib/cases";
import { buildReport, currency, type Report } from "@/lib/report";
import {
  ANALYSIS_CONFIDENCE_LABEL,
  NOT_CALCULATED,
  confidenceBand,
  evidenceStrength,
  formatConfidencePct,
} from "@/lib/confidence/confidence-vocabulary";
import {
  useFullWorkspaceBundle,
  bundleToCase,
  type FullWorkspaceBundle,
} from "@/hooks/use-real-reviews";
import { useAuth } from "@/hooks/use-auth";
import { useReportPdfActions } from "@/hooks/use-report-pdf";
import type {
  TimelineEvent,
  InternalNote,
  ReviewFile,
  ReviewChecklist,
  ReviewStatus,
} from "@/lib/db/models";
import {
  CHECKLIST_ITEMS,
  WORKSPACE_STATUSES,
  addInternalNote,
  assignReview,
  changeReviewStatus,
  markReviewComplete,
  saveChecklist,
} from "@/lib/workspace-actions";

export const Route = createFileRoute("/workspace/$id")({
  head: ({ params }) => ({
    meta: [
      { title: `ClaimGuard | Review Workspace — ${params.id}` },
      {
        name: "description",
        content:
          "Internal review workspace for validating findings, preparing reports, and managing negotiations.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => (
    <AuthGate allow={["staff", "admin"]} allowDemo>
      <ReviewWorkspacePage />
    </AuthGate>
  ),
});

/* ─────────────────────────────────────────────────────────── */
/* Mock fallback (works without a real case)                   */
/* ─────────────────────────────────────────────────────────── */

function mockCase(id: string): Case {
  return {
    id,
    createdAt: new Date().toISOString(),
    method: "upload",
    fastTrack: true,
    files: [
      { name: "invoice.pdf", size: 218_431, type: "application/pdf" },
      { name: "release_notice.jpg", size: 984_112, type: "image/jpeg" },
      { name: "tow_receipt.pdf", size: 129_004, type: "application/pdf" },
    ],
    details: {
      vendor: "SafeMove Storage — Austin",
      invoiceDate: "Mar 14, 2026",
      invoiceType: "Storage Invoice",
      totalAmount: "4865",
      urgency: "high",
      timeframe: "24-48 hours",
      zip: "78704",
      notes:
        "Vehicle released 3 days after payment. Rate seems higher than published on their website.",
    },
    status: "reviewing",
    negotiationChosen: false,
  };
}

/* ─────────────────────────────────────────────────────────── */
/* Page                                                        */
/* ─────────────────────────────────────────────────────────── */

function ReviewWorkspacePage() {
  const { id } = useParams({ from: "/workspace/$id" });
  const bundle = useFullWorkspaceBundle(id);
  const realFromDb = useMemo(() => bundleToCase(bundle), [bundle]);
  const localReal = useCase(id);
  const c = realFromDb ?? localReal ?? mockCase(id);
  const report = useMemo(() => buildReport(c), [c]);
  const { isStaff, isAdmin } = useAuth();
  const canSeeReadiness = isStaff || isAdmin;
  const readiness = (bundle.review as { readiness?: unknown } | null)?.readiness as
    | import("@/lib/readiness/types").ReportReadiness
    | null
    | undefined;

  if (bundle.loading && !localReal) {
    return <WorkspaceSkeleton />;
  }

  return (
    <div className="min-h-dvh bg-secondary/30">
      <SiteHeader />
      <ReviewHeader c={c} report={report} bundle={bundle} />

      <div className="container-page py-4 md:py-5">
        <div className="mb-3">
          <Breadcrumbs
            items={[{ label: "Operations", to: "/operations" }, { label: `Review ${c.id}` }]}
          />
        </div>

        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="min-w-0 space-y-5">
            <Panel
              id="invoice"
              title="Invoice Viewer"
              subtitle="Primary invoice and supporting documents."
              icon={<FileText className="h-4 w-4" />}
              defaultOpen
              size="lg"
            >
              <InvoiceViewer c={c} />
            </Panel>

            <Panel
              id="findings"
              title="AI Findings"
              subtitle="Intelligence, scores, and prioritized opportunities."
              icon={<Sparkles className="h-4 w-4" />}
              defaultOpen
              accent="trust"
            >
              <AIFindings report={report} />
            </Panel>

            <Panel
              id="zip"
              title="ZIP Code Market Comparison"
              subtitle={`Local benchmarks for ${c.details.zip ?? "—"}.`}
              icon={<MapPin className="h-4 w-4" />}
              defaultOpen
            >
              <ZipComparison report={report} c={c} />
            </Panel>

            <Panel
              id="laws"
              title="State Laws & Regulations"
              subtitle="Applicable statutes and negotiation impact."
              icon={<Scale className="h-4 w-4" />}
            >
              <LawsPanel report={report} />
            </Panel>

            <div className="grid gap-5 md:grid-cols-2">
              <Panel
                id="customer"
                title="Customer Information"
                subtitle="Contact, references, and notes."
                icon={<User className="h-4 w-4" />}
              >
                <CustomerInfo c={c} bundle={bundle} />
              </Panel>

              <Panel
                id="checklist"
                title="Internal Checklist"
                subtitle="Review readiness."
                icon={<ClipboardCheck className="h-4 w-4" />}
                accent="savings"
              >
                <InternalChecklist bundle={bundle} />
              </Panel>
            </div>

            {canSeeReadiness && (
              <Panel
                id="readiness"
                title="Report Readiness Gate"
                subtitle="Internal QA — module, chapter, and consistency verdict for this claim."
                icon={<ShieldCheck className="h-4 w-4" />}
                accent="trust"
              >
                <ReadinessPanel readiness={readiness ?? null} />
              </Panel>
            )}

            <Panel
              id="timeline"
              title="Timeline Analysis"
              subtitle="Every key date across the case."
              icon={<CalendarDays className="h-4 w-4" />}
            >
              <TimelineAnalysis bundle={bundle} />
            </Panel>

            <Panel
              id="negotiation"
              title="Negotiation Builder"
              subtitle="Arguments, evidence, and draft letter."
              icon={<Handshake className="h-4 w-4" />}
              accent="negotiate"
            >
              <NegotiationBuilder report={report} />
            </Panel>

            <Panel
              id="report"
              title="Professional Findings Report Preview"
              subtitle="Live preview of the customer-facing report."
              icon={<BadgeCheck className="h-4 w-4" />}
            >
              <ReportPreview id={c.id} report={report} />
            </Panel>

            <Panel
              id="activity"
              title="Activity Timeline"
              subtitle="Every action taken on this review."
              icon={<Activity className="h-4 w-4" />}
            >
              <ActivityTimeline bundle={bundle} />
            </Panel>
          </div>

          <aside className="lg:sticky lg:top-[140px] lg:self-start">
            <QuickActionsSidebar reviewId={bundle.review?.id ?? id} />
          </aside>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Sticky Review Header                                        */
/* ─────────────────────────────────────────────────────────── */

function ReviewHeader({
  c,
  report,
  bundle,
}: {
  c: Case;
  report: Report;
  bundle: FullWorkspaceBundle;
}) {
  const { user, isStaff, isAdmin } = useAuth();
  const canAct = !!bundle.review && (isStaff || isAdmin);
  const [busy, setBusy] = useState<string | null>(null);
  const r = bundle.review;
  const pdfActions = useReportPdfActions(r?.id ?? c.id);
  const statusLabel = r ? prettyStatus(r.status) : "In Review";
  const statusTone: Tone =
    r?.status === "completed"
      ? "savings"
      : r?.status === "cancelled"
        ? "amber"
        : r?.status === "negotiating"
          ? "negotiate"
          : "trust";
  const vendor = r?.vendor ?? report.vendor;
  const customerLabel = r ? "Customer" : "Rachel Kim";
  const orgLabel = r ? "Live review" : "Kim Household · Personal";
  const assignedLabel = r?.assigned_staff_id
    ? r.assigned_staff_id === user?.id
      ? "You"
      : `#${r.assigned_staff_id.slice(0, 6)}`
    : "Unassigned";

  async function runAction(label: string, fn: () => Promise<void>) {
    if (!r) return;
    setBusy(label);
    try {
      await fn();
      bundle.refresh();
      toast.success(`${label} saved`);
    } catch (e) {
      toast.error(`${label} failed`, {
        description: (e as Error).message,
      });
    } finally {
      setBusy(null);
    }
  }

  async function onStatusChange(next: string) {
    if (!r || next === r.status) return;
    await runAction("Status updated", () =>
      changeReviewStatus(r.id, next as ReviewStatus, user?.id ?? null),
    );
  }

  async function onAssignMe() {
    if (!r) return;
    await runAction("Assignment saved", () =>
      assignReview(r.id, user?.id ?? null, user?.id ?? null),
    );
  }

  async function onMarkComplete() {
    if (!r) return;
    if (!window.confirm(`Mark ${r.file_number} as completed?`)) return;
    await runAction("Review completed", () => markReviewComplete(r.id, user?.id ?? null));
  }

  async function onProcessNextAI() {
    if (!r) return;
    setBusy("Process next AI step");
    try {
      const { runNextAIJob } = await import("@/lib/ai.functions");
      const result = await runNextAIJob();
      bundle.refresh();
      if (!result) {
        toast.info("No queued AI jobs for this review.");
      } else if (result.outcome === "completed") {
        toast.success(`Completed: ${result.job.job_type}`);
      } else if (result.outcome === "retrying") {
        toast.info("Waiting on a dependency — will retry.");
      } else {
        toast.error(`Job failed: ${result.job.job_type}`, {
          description: result.job.error ?? undefined,
        });
      }
    } catch (e) {
      toast.error("Couldn't run next AI job", {
        description: (e as Error).message,
      });
    } finally {
      setBusy(null);
    }
  }

  const submittedAt = r?.created_at ?? c.createdAt;
  const submittedLabel = submittedAt
    ? new Date(submittedAt).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "—";

  return (
    <div className="sticky top-0 z-40 border-b border-border/70 bg-card/95 shadow-sm backdrop-blur">
      <div className="container-page py-2.5">
        <div className="grid gap-3 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)_auto] xl:items-center">
          {/* LEFT — identity */}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
              <span className="font-mono text-[11px] font-semibold text-navy">{c.id}</span>
              <span className="inline-flex items-center gap-1 text-navy">
                <Building2 className="h-3 w-3 text-muted-foreground" />
                <span className="truncate font-semibold">{vendor}</span>
              </span>
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                <User className="h-3 w-3" />
                {customerLabel}
              </span>
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                <CalendarDays className="h-3 w-3" />
                {submittedLabel}
              </span>
              <SpecialistChip name={assignedLabel} />
            </div>
          </div>

          {/* CENTER — status + metrics */}
          <div className="flex flex-wrap items-center gap-1.5 xl:justify-center">
            <StatusPill label={statusLabel} tone={statusTone} />
            {/* SAB-004: bound to the canonical findings confidence — no hardcoded pills. */}
            <MiniStat
              label={ANALYSIS_CONFIDENCE_LABEL}
              value={confidenceBand(report.confidence) ?? NOT_CALCULATED}
              tone="trust"
            />
            <MiniStat
              label="Evidence"
              value={evidenceStrength(report.confidence) ?? NOT_CALCULATED}
              tone="navy"
            />
            <MiniStat
              label="Savings"
              value={`${currency(report.potentialSavings[0])}–${currency(
                report.potentialSavings[1],
              )}`}
              tone="savings"
            />
            <MiniStat label="Health" value="Strong" tone="savings" />
          </div>

          {/* RIGHT — primary actions */}
          <div className="flex flex-wrap items-center gap-1.5 xl:justify-end">
            {canAct && r ? (
              <select
                value={r.status}
                disabled={busy !== null}
                onChange={(e) => onStatusChange(e.target.value)}
                className="h-7 rounded-md border border-border bg-card px-2 text-[11px] font-semibold text-navy shadow-sm transition hover:bg-secondary/60 focus:border-navy/30 focus:outline-none disabled:opacity-60"
                aria-label="Change review status"
              >
                {WORKSPACE_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
                {!WORKSPACE_STATUSES.some((s) => s.value === r.status) && (
                  <option value={r.status}>{prettyStatus(r.status)}</option>
                )}
              </select>
            ) : null}
            <HeaderBtn icon={<Save className="h-3.5 w-3.5" />}>Save</HeaderBtn>
            <HeaderBtn
              icon={<UserPlus className="h-3.5 w-3.5" />}
              onClick={canAct ? onAssignMe : undefined}
            >
              {canAct && r?.assigned_staff_id === user?.id ? "Assigned" : "Assign"}
            </HeaderBtn>
            <HeaderBtn icon={<Mail className="h-3.5 w-3.5" />}>Email</HeaderBtn>
            <HeaderBtn icon={<PenLine className="h-3.5 w-3.5" />} tone="negotiate">
              Letter
            </HeaderBtn>
            <HeaderBtn
              icon={<FileText className="h-3.5 w-3.5" />}
              tone="trust"
              onClick={pdfActions.busy ? undefined : pdfActions.downloadOrGenerate}
            >
              {pdfActions.busy ? "Preparing…" : "Report"}
            </HeaderBtn>
            <HeaderBtn
              icon={<Zap className="h-3.5 w-3.5" />}
              tone="trust"
              onClick={canAct ? onProcessNextAI : undefined}
            >
              Next AI
            </HeaderBtn>
            <HeaderBtn
              icon={<CheckCircle2 className="h-3.5 w-3.5" />}
              tone="savings"
              onClick={canAct ? onMarkComplete : undefined}
            >
              Complete
            </HeaderBtn>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ label, tone }: { label: string; tone: Tone }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] ${toneChip(tone)}`}
    >
      <Circle className="h-2 w-2 fill-current" />
      {label}
    </span>
  );
}

function SpecialistChip({ name }: { name: string }) {
  const initials = name
    .split(" ")
    .map((s) => s[0])
    .join("");
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-2 py-0.5 text-[11px] font-semibold text-navy">
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-navy text-[10px] text-white">
        {initials}
      </span>
      {name}
    </span>
  );
}

function MiniStat({ label, value, tone }: { label: string; value: string; tone: Tone }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${toneBorder(tone)}`}
    >
      <span className="text-muted-foreground">{label}</span>
      <span className="text-navy">{value}</span>
    </span>
  );
}

function HeaderBtn({
  icon,
  children,
  tone,
  onClick,
}: {
  icon: ReactNode;
  children: ReactNode;
  tone?: "trust" | "negotiate" | "savings";
  onClick?: () => void;
}) {
  const cls =
    tone === "negotiate"
      ? "bg-purple-600 text-white hover:bg-purple-700"
      : tone === "trust"
        ? "bg-navy text-white hover:bg-navy/90"
        : tone === "savings"
          ? "bg-savings text-savings-foreground hover:bg-savings/90"
          : "border border-border bg-white text-navy hover:bg-secondary/60";
  return (
    <button
      type="button"
      onClick={
        onClick ??
        (() =>
          toast(typeof children === "string" ? children : "Action queued", {
            description: "Placeholder — API integration pending.",
          }))
      }
      className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold shadow-sm transition ${cls}`}
    >
      {icon}
      {children}
    </button>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Panel primitive                                             */
/* ─────────────────────────────────────────────────────────── */

type Tone = "navy" | "trust" | "savings" | "negotiate" | "amber";

function toneChip(t: Tone) {
  return {
    navy: "bg-navy/10 text-navy",
    trust: "bg-blue-50 text-blue-700",
    savings: "bg-emerald-50 text-emerald-700",
    negotiate: "bg-purple-50 text-purple-700",
    amber: "bg-amber-50 text-amber-700",
  }[t];
}
function toneBorder(t: Tone) {
  return {
    navy: "border-navy/15 bg-navy/5",
    trust: "border-blue-100 bg-blue-50/60",
    savings: "border-emerald-100 bg-emerald-50/60",
    negotiate: "border-purple-100 bg-purple-50/60",
    amber: "border-amber-100 bg-amber-50/60",
  }[t];
}

function Panel({
  title,
  subtitle,
  icon,
  children,
  defaultOpen,
  accent = "navy",
  size,
}: {
  id: string;
  title: string;
  subtitle?: string;
  icon: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  accent?: Tone;
  size?: "lg";
}) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <section
      className={`overflow-hidden rounded-2xl border border-border bg-white shadow-card transition ${size === "lg" ? "" : ""}`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-3 border-b border-transparent px-6 py-4 text-left transition hover:bg-secondary/40"
      >
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${toneChip(accent)}`}
        >
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate font-display text-base text-navy">{title}</div>
          {subtitle && <div className="truncate text-xs text-muted-foreground">{subtitle}</div>}
        </div>
        {open ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>
      {open && <div className="border-t border-border/60 p-6">{children}</div>}
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Panel 1 — Invoice Viewer                                    */
/* ─────────────────────────────────────────────────────────── */

function InvoiceViewer({ c }: { c: Case }) {
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(100);
  const totalPages = 3;
  const files =
    c.files.length > 0 ? c.files : [{ name: "invoice.pdf", size: 218431, type: "application/pdf" }];
  const [active, setActive] = useState(files[0].name);

  return (
    <div className="grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)]">
      {/* Left rail: files + thumbnails */}
      <div className="space-y-4">
        <div>
          <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
            Attachments
          </div>
          <ul className="space-y-1">
            {files.map((f) => (
              <li key={f.name}>
                <button
                  type="button"
                  onClick={() => setActive(f.name)}
                  className={`flex w-full items-center gap-2 rounded-md border px-2.5 py-2 text-left text-xs transition ${
                    active === f.name
                      ? "border-navy/20 bg-navy/5 text-navy"
                      : "border-border bg-white text-muted-foreground hover:bg-secondary/50"
                  }`}
                >
                  {f.type.startsWith("image") ? (
                    <FileImage className="h-3.5 w-3.5 shrink-0" />
                  ) : (
                    <FileText className="h-3.5 w-3.5 shrink-0" />
                  )}
                  <span className="truncate">{f.name}</span>
                </button>
              </li>
            ))}
            <li>
              <button
                type="button"
                className="mt-1 flex w-full items-center gap-2 rounded-md border border-dashed border-border px-2.5 py-2 text-xs text-muted-foreground hover:bg-secondary/50"
              >
                <Upload className="h-3.5 w-3.5" />
                Add file
              </button>
            </li>
          </ul>
        </div>

        <div>
          <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
            Pages
          </div>
          <div className="grid grid-cols-3 gap-2 lg:grid-cols-2">
            {Array.from({ length: totalPages }).map((_, i) => {
              const n = i + 1;
              return (
                <button
                  type="button"
                  key={n}
                  onClick={() => setPage(n)}
                  className={`aspect-[3/4] rounded border text-[10px] font-semibold transition ${
                    page === n
                      ? "border-navy bg-navy/5 text-navy"
                      : "border-border bg-secondary/50 text-muted-foreground hover:border-navy/30"
                  }`}
                >
                  Page {n}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Viewer */}
      <div className="min-w-0">
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-t-xl border border-border bg-secondary/50 px-3 py-2 text-xs">
          <div className="flex items-center gap-1">
            <IconBtn label="Previous page" onClick={() => setPage((p) => Math.max(1, p - 1))}>
              <ChevronLeft className="h-3.5 w-3.5" />
            </IconBtn>
            <span className="px-1 font-mono text-navy">
              {page} / {totalPages}
            </span>
            <IconBtn label="Next page" onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
              <ChevronRight className="h-3.5 w-3.5" />
            </IconBtn>
          </div>
          <div className="flex items-center gap-1">
            <IconBtn label="Zoom out" onClick={() => setZoom((z) => Math.max(50, z - 10))}>
              <ZoomOut className="h-3.5 w-3.5" />
            </IconBtn>
            <span className="w-10 text-center font-mono text-navy">{zoom}%</span>
            <IconBtn label="Zoom in" onClick={() => setZoom((z) => Math.min(200, z + 10))}>
              <ZoomIn className="h-3.5 w-3.5" />
            </IconBtn>
            <IconBtn label="Rotate document">
              <RotateCw className="h-3.5 w-3.5" />
            </IconBtn>
            <IconBtn label="Download document">
              <Download className="h-3.5 w-3.5" />
            </IconBtn>
            <IconBtn label="Open full screen">
              <Maximize2 className="h-3.5 w-3.5" />
            </IconBtn>
          </div>
        </div>

        <div className="rounded-b-xl border border-t-0 border-border bg-secondary/30 p-6">
          <div
            className="mx-auto max-w-md rounded-lg border border-border bg-white p-6 shadow-card"
            style={{ transform: `scale(${zoom / 100})`, transformOrigin: "top center" }}
          >
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
              Invoice
            </div>
            <div className="mt-1 font-display text-lg text-navy">
              {c.details.vendor ?? "Vendor"}
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              Invoice #A-38210 • {c.details.invoiceDate ?? "—"}
            </div>
            <div className="mt-4 space-y-2 border-t border-border/60 pt-3 text-xs">
              {[
                ["Tow — Standard", "$285.00"],
                ["Storage (28 days)", "$4,200.00"],
                ["Administrative Fee", "$125.00"],
                ["Environmental Fee", "$75.00"],
                ["Lien Fee", "$15.00"],
                ["Release Documentation", "$165.00"],
              ].map(([label, val]) => (
                <div key={label} className="flex items-center justify-between">
                  <span className="text-navy">{label}</span>
                  <span className="font-mono text-muted-foreground">{val}</span>
                </div>
              ))}
              <div className="flex items-center justify-between border-t border-border/60 pt-2 font-semibold text-navy">
                <span>Total</span>
                <span className="font-mono">
                  {currency(parseFloat(c.details.totalAmount ?? "4865"))}
                </span>
              </div>
            </div>
            <div className="mt-4 rounded-md bg-secondary/60 px-3 py-2 text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
              OCR extraction ready — API integration pending
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function IconBtn({
  children,
  onClick,
  label,
}: {
  children: ReactNode;
  onClick?: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="flex h-7 w-7 items-center justify-center rounded-md border border-transparent text-navy transition hover:border-border hover:bg-white"
    >
      {children}
    </button>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Panel 2 — AI Findings                                       */
/* ─────────────────────────────────────────────────────────── */

function AIFindings({ report }: { report: Report }) {
  return (
    <div className="space-y-6">
      {/* Score tiles */}
      <div className="grid gap-4 md:grid-cols-3">
        <ScoreTile
          label="Evidence Strength"
          value={evidenceStrength(report.confidence) ?? NOT_CALCULATED}
          hint="Derived from the findings-engine confidence."
          tone="navy"
        />
        <ScoreTile
          label={ANALYSIS_CONFIDENCE_LABEL}
          value={formatConfidencePct(report.confidence)}
          hint={
            confidenceBand(report.confidence)
              ? `${confidenceBand(report.confidence)} confidence`
              : "Awaiting completed analysis"
          }
          tone="trust"
        >
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-secondary">
            <div
              className="h-full rounded-full bg-blue-600"
              style={{ width: `${report.confidence}%` }}
            />
          </div>
        </ScoreTile>
        <ScoreTile label="Review Health" value="Strong" hint="Ready to negotiate" tone="savings" />
      </div>

      {/* Executive summary */}
      <div className="rounded-xl border border-border bg-secondary/40 p-5">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
          AI Executive Summary
        </div>
        <p className="mt-2 text-sm leading-relaxed text-navy">
          Storage charges represent the largest opportunity. The daily rate exceeds the local market
          by approximately <span className="font-semibold">43%</span>, and the invoice includes
          administrative and environmental fees that may not be authorized under state code.
          Estimated recoverable range:{" "}
          <span className="font-semibold text-savings">
            {currency(report.potentialSavings[0])}–{currency(report.potentialSavings[1])}
          </span>
          .
        </p>
      </div>

      {/* Heat map + confidence details */}
      <div className="grid gap-4 lg:grid-cols-2">
        <HeatMap />
        <ConfidencePanel confidence={report.confidence} />
      </div>

      {/* Categorized findings */}
      <div className="grid gap-4 lg:grid-cols-2">
        <FindingGroup
          title="Top Findings"
          icon={<Flame className="h-4 w-4" />}
          tone="amber"
          items={[
            "Storage rate 43% above local median.",
            "Administrative fee not authorized under Tex. Occ. Code Ch. 2308 / 16 TAC Ch. 85.",
            "Environmental fee — no supporting docs.",
          ]}
        />
        <FindingGroup
          title="Unique Charges Flagged"
          icon={<Receipt className="h-4 w-4" />}
          tone="amber"
          items={[
            "Lien fee appears to duplicate release fee.",
            "Weekend surcharge — no policy on file.",
            "Rounded storage days (28 vs 26 actual).",
          ]}
        />
        <FindingGroup
          title="Missing Documentation"
          icon={<AlertTriangle className="h-4 w-4" />}
          tone="amber"
          items={[
            "No signed authorization to tow.",
            "Release notice — timestamp missing.",
            "Rate card not posted at facility.",
          ]}
        />
        <FindingGroup
          title="Timeline Concerns"
          icon={<Clock className="h-4 w-4" />}
          tone="trust"
          items={[
            "3-day gap between payment and release.",
            "Notice sent 4 days after impound.",
            "Storage billed for weekend of closure.",
          ]}
        />
        <FindingGroup
          title="Market Pricing Flags"
          icon={<MapPin className="h-4 w-4" />}
          tone="trust"
          items={[
            "78704 median $65/day storage.",
            "Invoice rate $150/day (+130%).",
            "Comparable vendors within 3 miles.",
          ]}
        />
        <FindingGroup
          title="Negotiation Opportunities"
          icon={<Handshake className="h-4 w-4" />}
          tone="negotiate"
          items={[
            "Strong basis to reduce storage 40–50%.",
            "Request full waiver of admin fee.",
            "Environmental fee: refund likely.",
          ]}
        />
      </div>

      <RecommendationCard />
    </div>
  );
}

function ScoreTile({
  label,
  value,
  hint,
  tone,
  children,
}: {
  label: string;
  value: string;
  hint: string;
  tone: Tone;
  children?: ReactNode;
}) {
  return (
    <div className={`rounded-xl border p-5 ${toneBorder(tone)}`}>
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-2 font-display text-3xl text-navy">{value}</div>
      <div className="mt-1 text-xs text-muted-foreground">{hint}</div>
      {children}
    </div>
  );
}

function HeatMap() {
  const rows: [string, number, Tone][] = [
    ["Storage Fees", 5, "amber"],
    ["Documentation", 5, "amber"],
    ["Administrative Fees", 4, "amber"],
    ["Timeline", 3, "trust"],
    ["Environmental Fees", 2, "trust"],
    ["Release Charges", 3, "trust"],
  ];
  return (
    <div className="rounded-xl border border-border bg-white p-5">
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
        <Flame className="h-3.5 w-3.5" />
        Opportunity Heat Map
      </div>
      <ul className="mt-3 space-y-2.5">
        {rows.map(([label, heat, tone]) => (
          <li key={label} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
            <span className="truncate text-sm text-navy">{label}</span>
            <span className="flex items-center gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <span
                  key={i}
                  className={`h-2.5 w-2.5 rounded-sm ${
                    i < heat ? (tone === "amber" ? "bg-amber-500" : "bg-blue-500") : "bg-secondary"
                  }`}
                />
              ))}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ConfidencePanel({ confidence }: { confidence: number }) {
  const reasons = [
    "Applicable laws identified",
    "Local pricing available",
    "Timeline supports review",
    "Documentation incomplete",
  ];
  return (
    <div className="rounded-xl border border-border bg-white p-5">
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
        <ShieldCheck className="h-3.5 w-3.5" />
        Analysis Confidence Meter
      </div>
      <div className="mt-3 flex items-end justify-between">
        <div className="font-display text-4xl text-navy">
          {formatConfidencePct(confidence)}
        </div>
        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-700">
          {confidenceBand(confidence)
            ? `${confidenceBand(confidence)} confidence`
            : "Not calculated"}
        </span>
      </div>
      <div className="mt-3 h-2 overflow-hidden rounded-full bg-secondary">
        <div className="h-full rounded-full bg-emerald-500" style={{ width: `${confidence}%` }} />
      </div>
      <ul className="mt-4 space-y-1.5 text-sm text-navy">
        {reasons.map((r) => (
          <li key={r} className="flex items-center gap-2">
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
            {r}
          </li>
        ))}
      </ul>
    </div>
  );
}

function FindingGroup({
  title,
  icon,
  items,
  tone,
}: {
  title: string;
  icon: ReactNode;
  items: string[];
  tone: Tone;
}) {
  return (
    <div className="rounded-xl border border-border bg-white p-5">
      <div className="flex items-center gap-2">
        <div className={`flex h-7 w-7 items-center justify-center rounded-lg ${toneChip(tone)}`}>
          {icon}
        </div>
        <div className="font-semibold text-navy">{title}</div>
      </div>
      <ul className="mt-3 space-y-2 text-sm text-navy">
        {items.map((it) => (
          <li key={it} className="flex gap-2">
            <span
              className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${
                tone === "amber"
                  ? "bg-amber-500"
                  : tone === "negotiate"
                    ? "bg-purple-500"
                    : "bg-blue-500"
              }`}
            />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RecommendationCard() {
  const recs = [
    "Review storage duration and verify actual days billed.",
    "Request supporting documentation for admin & environmental fees.",
    "Compare local storage pricing before initiating negotiation.",
  ];
  return (
    <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-5">
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-blue-700">
        <Sparkles className="h-3.5 w-3.5" />
        AI Recommendations
      </div>
      <ol className="mt-3 space-y-2 text-sm text-navy">
        {recs.map((r, i) => (
          <li key={r} className="flex gap-2">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white text-[11px] font-semibold text-blue-700">
              {i + 1}
            </span>
            {r}
          </li>
        ))}
      </ol>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Panel 3 — Customer                                          */
/* ─────────────────────────────────────────────────────────── */

function CustomerInfo({ c, bundle }: { c: Case; bundle: FullWorkspaceBundle }) {
  const r = bundle.review;
  const isReal = !!r;
  const customerName = isReal ? `Customer #${r!.customer_id.slice(0, 8)}` : "Rachel Kim";
  const orgName = isReal
    ? r!.organization_id
      ? "Organization on file"
      : "Individual"
    : "Kim Household";
  const email = isReal ? "On file (protected)" : "rachel.kim@example.com";
  const phone = isReal ? "—" : "(512) 555-0138";
  const ref = isReal ? r!.file_number : "CLM-2026-88214";
  const prev = isReal ? "—" : "2 completed";
  const uploaded = bundle.files.length
    ? bundle.files.map((f) => ({ name: f.file_name }))
    : c.files.length
      ? c.files.map((f) => ({ name: f.name }))
      : [{ name: "invoice.pdf" }];
  const customerNotes = r?.notes ?? c.details.notes ?? "";
  const latestInternalNote = bundle.notes[0]?.body ?? "";

  const fields: [ReactNode, string, string][] = [
    [<User className="h-3.5 w-3.5" />, "Customer", customerName],
    [<Building2 className="h-3.5 w-3.5" />, "Organization", orgName],
    [<Mail className="h-3.5 w-3.5" />, "Email", email],
    [<Phone className="h-3.5 w-3.5" />, "Phone", phone],
    [<Hash className="h-3.5 w-3.5" />, "Reference", ref],
    [<Upload className="h-3.5 w-3.5" />, "Submission Method", c.method],
    [
      <Mail className="h-3.5 w-3.5" />,
      "Private Submission Email",
      `${c.id.toLowerCase()}@review.mail`,
    ],
    [<ClipboardList className="h-3.5 w-3.5" />, "Previous Reviews", prev],
  ];
  return (
    <div className="space-y-4">
      <dl className="grid gap-3 sm:grid-cols-2">
        {fields.map(([icon, label, val]) => (
          <div key={label} className="rounded-lg border border-border bg-secondary/30 px-3 py-2.5">
            <dt className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {icon}
              {label}
            </dt>
            <dd className="mt-1 truncate text-sm text-navy">{val}</dd>
          </div>
        ))}
      </dl>

      <div>
        <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
          Uploaded Files
        </div>
        <ul className="space-y-1.5">
          {uploaded.map((f) => (
            <li
              key={f.name}
              className="flex items-center justify-between rounded-md border border-border bg-white px-3 py-2 text-xs"
            >
              <span className="flex items-center gap-2 text-navy">
                <FileText className="h-3.5 w-3.5" />
                {f.name}
              </span>
              <button className="text-muted-foreground hover:text-navy">
                <Download className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      </div>

      <NotesEditor
        label="Customer Notes"
        icon={<StickyNote className="h-3.5 w-3.5" />}
        placeholder="Customer-provided context…"
        defaultValue={customerNotes}
      />
      <InternalNotesSection bundle={bundle} />
      {/* keep the referenced variable used */}
      {latestInternalNote ? null : null}
    </div>
  );
}

function NotesEditor({
  label,
  icon,
  placeholder,
  defaultValue,
}: {
  label: string;
  icon: ReactNode;
  placeholder: string;
  defaultValue?: string;
}) {
  const [v, setV] = useState(defaultValue ?? "");
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
        {icon}
        {label}
      </div>
      <textarea
        value={v}
        onChange={(e) => setV(e.target.value)}
        placeholder={placeholder}
        rows={3}
        className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-navy placeholder:text-muted-foreground focus:border-navy/30 focus:outline-none"
      />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Panel 4 — Laws                                              */
/* ─────────────────────────────────────────────────────────── */

function LawsPanel({ report }: { report: Report }) {
  const laws = [
    {
      name: "Tex. Occ. Code Ch. 2308 — Towing & Booting Act",
      summary: "Governs non-consent tows and permissible fees.",
      matters: "Limits recoverable charges; excludes unauthorized admin fees.",
      impact: "Strong basis to challenge admin fee ($125).",
      ref: "TX-OCC-2308",
    },
    {
      name: "16 TAC Ch. 85 — TDLR Tow & VSF Rules",
      summary: "Sets required disclosures, non-consent tow fee schedule, and VSF storage rules.",
      matters: "Requires posted rate schedule at facility; supports rate audit.",
      impact: "Applies to $4,200 storage line; potential 25–40% reduction.",
      ref: "16-TAC-85",
    },
    {
      name: "State Property Code — Notice Requirements",
      summary: "Notice of impound must be sent within statutory window.",
      matters: "Late notice can void portions of accrued storage.",
      impact: "Timeline gap of 4 days — voids ~$300 storage.",
      ref: "PROP-CODE-70.006",
    },
    {
      name: "Deceptive Trade Practices Act",
      summary: "Prohibits unconscionable pricing and undisclosed charges.",
      matters: "Supports environmental fee challenge (no disclosure).",
      impact: "Full refund likely for $75 environmental line.",
      ref: "DTPA-17.46",
    },
  ];
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {laws.map((l) => (
        <article key={l.ref} className="flex flex-col rounded-xl border border-border bg-white p-5">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-navy/10 text-navy">
              <Landmark className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display text-base text-navy">{l.name}</div>
              <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                {l.ref}
              </div>
            </div>
          </div>
          <p className="mt-3 text-sm text-navy">{l.summary}</p>
          <div className="mt-3 space-y-2 text-xs">
            <LawMeta label="Why it matters" value={l.matters} />
            <LawMeta label="Negotiation impact" value={l.impact} tone="savings" />
          </div>
          <button
            type="button"
            className="mt-4 inline-flex items-center gap-1 self-start text-xs font-semibold text-navy hover:underline"
          >
            View reference <ExternalLink className="h-3 w-3" />
          </button>
        </article>
      ))}
      <div className="rounded-xl border border-dashed border-border bg-secondary/40 p-5 text-xs text-muted-foreground md:col-span-2">
        <Info className="mr-1 inline h-3.5 w-3.5" />
        Reference summaries only — not legal advice. Live legal API integration pending.{" "}
        {report.laws.length} statutes indexed for this jurisdiction.
      </div>
    </div>
  );
}

function LawMeta({ label, value, tone }: { label: string; value: string; tone?: "savings" }) {
  return (
    <div className="rounded-lg bg-secondary/40 px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </div>
      <div className={`mt-0.5 text-navy ${tone === "savings" ? "text-savings" : ""}`}>{value}</div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Panel 5 — ZIP Comparison                                    */
/* ─────────────────────────────────────────────────────────── */

function ZipComparison({ report, c }: { report: Report; c: Case }) {
  const rows = [
    { label: "Daily Storage Rate", invoice: 150, low: 55, high: 75, unit: "/day" },
    { label: "Administrative Fee", invoice: 125, low: 0, high: 45, unit: "" },
    { label: "Release / Lien Fee", invoice: 180, low: 65, high: 110, unit: "" },
    { label: "Environmental Fee", invoice: 75, low: 0, high: 25, unit: "" },
  ];
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <MapPin className="h-4 w-4 text-navy" />
        Benchmarks for ZIP <span className="font-mono text-navy">
          {c.details.zip ?? "78704"}
        </span> • {report.state}
      </div>
      <div className="grid gap-3">
        {rows.map((r) => {
          const invoice = r.invoice;
          const max = Math.max(invoice, r.high) * 1.15;
          const localMid = (r.low + r.high) / 2;
          const overpay = invoice - localMid;
          const pct = Math.round((overpay / (localMid || 1)) * 100);
          return (
            <div key={r.label} className="rounded-xl border border-border bg-white p-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="font-semibold text-navy">{r.label}</div>
                <div className="text-xs text-muted-foreground">
                  Invoice{" "}
                  <span className="font-mono text-navy">
                    ${invoice}
                    {r.unit}
                  </span>{" "}
                  · Local{" "}
                  <span className="font-mono text-navy">
                    ${r.low}–${r.high}
                    {r.unit}
                  </span>
                </div>
              </div>
              <div className="relative mt-3 h-3 rounded-full bg-secondary">
                <div
                  className="absolute inset-y-0 rounded-full bg-emerald-400/60"
                  style={{
                    left: `${(r.low / max) * 100}%`,
                    width: `${((r.high - r.low) / max) * 100}%`,
                  }}
                />
                <div
                  className="absolute inset-y-0 w-1 rounded-full bg-navy"
                  style={{ left: `${(invoice / max) * 100}%` }}
                  title={`Invoice $${invoice}`}
                />
              </div>
              <div className="mt-2 flex items-center justify-between text-[11px]">
                <span className="text-muted-foreground">Fair range shown in green</span>
                <span
                  className={overpay > 0 ? "font-semibold text-amber-700" : "text-muted-foreground"}
                >
                  {overpay > 0 ? `Over by $${overpay.toFixed(0)} (${pct}%)` : "Within range"}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Panel 6 — Timeline                                          */
/* ─────────────────────────────────────────────────────────── */

function TimelineAnalysis({ bundle }: { bundle: FullWorkspaceBundle }) {
  const realSteps = bundle.timeline.map((t) => ({
    label: t.title,
    date: new Date(t.created_at).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    }),
    icon: <Sparkles className="h-3.5 w-3.5" />,
    tone: "trust" as Tone,
    note: t.description ?? undefined,
  }));

  const placeholder: {
    label: string;
    date: string;
    icon: ReactNode;
    tone: Tone;
    note?: string;
  }[] = [
    {
      label: "Tow Date",
      date: "Feb 14, 2026",
      icon: <Truck className="h-3.5 w-3.5" />,
      tone: "navy",
    },
    {
      label: "Storage Begins",
      date: "Feb 14, 2026",
      icon: <Building2 className="h-3.5 w-3.5" />,
      tone: "navy",
    },
    {
      label: "Notice Sent",
      date: "Feb 18, 2026",
      icon: <Bell className="h-3.5 w-3.5" />,
      tone: "amber",
      note: "4-day delay — statutory concern.",
    },
    {
      label: "Invoice Issued",
      date: "Mar 14, 2026",
      icon: <Receipt className="h-3.5 w-3.5" />,
      tone: "navy",
    },
    {
      label: "Review Started",
      date: "Mar 15, 2026",
      icon: <Sparkles className="h-3.5 w-3.5" />,
      tone: "trust",
    },
    {
      label: "Negotiation",
      date: "In progress",
      icon: <Handshake className="h-3.5 w-3.5" />,
      tone: "negotiate",
    },
    {
      label: "Completed",
      date: "Pending",
      icon: <CheckCircle2 className="h-3.5 w-3.5" />,
      tone: "savings",
    },
  ];
  const steps = realSteps.length ? realSteps : placeholder;
  return (
    <ol className="relative space-y-4 border-l border-border pl-6">
      {steps.map((s) => (
        <li key={s.label} className="relative">
          <span
            className={`absolute -left-[30px] flex h-6 w-6 items-center justify-center rounded-full border-2 border-white shadow-sm ${toneChip(s.tone)}`}
          >
            {s.icon}
          </span>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div className="font-semibold text-navy">{s.label}</div>
            <div className="text-xs text-muted-foreground">{s.date}</div>
          </div>
          {s.note && (
            <div className="mt-1 rounded-md bg-amber-50 px-2.5 py-1 text-xs text-amber-800">
              {s.note}
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Panel 7 — Negotiation builder                               */
/* ─────────────────────────────────────────────────────────── */

function NegotiationBuilder({ report }: { report: Report }) {
  const [strategy, setStrategy] = useState<"soft" | "firm" | "escalate">("firm");
  return (
    <div className="space-y-5">
      <div className="grid gap-4 lg:grid-cols-2">
        <NegoBlock title="Strongest Arguments" icon={<Flame className="h-4 w-4" />}>
          <ul className="space-y-2 text-sm text-navy">
            <li>• Storage rate exceeds local median by 43%.</li>
            <li>• Notice sent outside statutory window.</li>
            <li>• Admin fee unauthorized under Tex. Occ. Code Ch. 2308.</li>
          </ul>
        </NegoBlock>
        <NegoBlock title="Supporting Laws" icon={<Scale className="h-4 w-4" />}>
          <ul className="space-y-2 text-sm text-navy">
            <li>• Tex. Occ. Code Ch. 2308 — Towing &amp; Booting Act</li>
            <li>• 16 TAC Ch. 85 — TDLR Tow &amp; VSF Rules</li>
            <li>• Tex. Prop. Code § 70.006 — Notice Requirements</li>
          </ul>
        </NegoBlock>
        <NegoBlock title="Market Comparison" icon={<MapPin className="h-4 w-4" />}>
          <div className="text-sm text-navy">
            Local storage median <span className="font-mono">$65/day</span> vs invoice{" "}
            <span className="font-mono">$150/day</span>. 12 comparable vendors within 3 miles.
          </div>
        </NegoBlock>
        <NegoBlock title="Supporting Documentation" icon={<ClipboardCheck className="h-4 w-4" />}>
          <ul className="space-y-2 text-sm text-navy">
            <li>• invoice.pdf — annotated</li>
            <li>• release_notice.jpg — timestamp gap</li>
            <li>• tow_receipt.pdf — signature missing</li>
          </ul>
        </NegoBlock>
      </div>

      <div className="rounded-xl border border-border bg-secondary/40 p-5">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
          Negotiation Strategy
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {(["soft", "firm", "escalate"] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStrategy(s)}
              className={`rounded-full border px-3 py-1 text-xs font-semibold capitalize transition ${
                strategy === s
                  ? "border-navy bg-navy text-white"
                  : "border-border bg-white text-navy hover:bg-white"
              }`}
            >
              {s === "escalate" ? "Escalate to counsel" : s + " tone"}
            </button>
          ))}
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div className="rounded-lg border border-border bg-white p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
              Counter Offer
            </div>
            <div className="mt-2 font-display text-2xl text-navy">
              {currency(report.fairRange[0])}
            </div>
            <div className="text-xs text-muted-foreground">
              Fair range {currency(report.fairRange[0])}–{currency(report.fairRange[1])}
            </div>
          </div>
          <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-emerald-700">
              Projected Savings
            </div>
            <div className="mt-2 font-display text-2xl text-emerald-700">
              {currency(report.potentialSavings[0])}–{currency(report.potentialSavings[1])}
            </div>
            <div className="text-xs text-emerald-800/80">
              {report.savingsPct[0]}–{report.savingsPct[1]}% off invoice
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-white p-5">
        <div className="flex items-center justify-between">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
            Generated Letter Preview
          </div>
          <div className="flex items-center gap-1.5">
            <IconBtn label="Copy letter text">
              <Copy className="h-3.5 w-3.5" />
            </IconBtn>
            <IconBtn label="Download letter">
              <Download className="h-3.5 w-3.5" />
            </IconBtn>
            <IconBtn label="Send letter">
              <Send className="h-3.5 w-3.5" />
            </IconBtn>
          </div>
        </div>
        <div className="mt-3 space-y-2 rounded-lg bg-secondary/40 p-4 text-sm text-navy">
          <div className="mb-2 inline-flex items-center gap-2 rounded-md bg-warn/10 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-warn">
            <AlertTriangle className="h-3 w-3" /> Draft — Not for Distribution
          </div>
          <p>To: Accounts, {report.vendor}</p>
          <p>Re: Invoice A-38210 — request for correction</p>
          <p>
            After a professional review of invoice A-38210, we identified charges that appear
            inconsistent with Tex. Occ. Code Ch. 2308 and 16 TAC Ch. 85. The daily
            storage rate exceeds the local median by approximately 43%…
          </p>
          <p className="text-muted-foreground">
            Draft preview only — final letter is generated by a specialist before sending.
          </p>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <HeaderBtn icon={<PenLine className="h-3.5 w-3.5" />} tone="negotiate">
            Generate Letter
          </HeaderBtn>
          <HeaderBtn icon={<Copy className="h-3.5 w-3.5" />}>Copy</HeaderBtn>
          <HeaderBtn icon={<Download className="h-3.5 w-3.5" />}>Download</HeaderBtn>
          <HeaderBtn icon={<Clock className="h-3.5 w-3.5" />}>Send Later</HeaderBtn>
        </div>
      </div>
    </div>
  );
}

function NegoBlock({
  title,
  icon,
  children,
}: {
  title: string;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border bg-white p-5">
      <div className="flex items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-purple-50 text-purple-700">
          {icon}
        </div>
        <div className="font-semibold text-navy">{title}</div>
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Panel 8 — Report preview                                    */
/* ─────────────────────────────────────────────────────────── */

function ReportPreview({ id, report }: { id: string; report: Report }) {
  return (
    <div className="space-y-3">
      <div className="max-h-[420px] overflow-auto rounded-xl border border-border bg-secondary/30 p-6">
        <div className="mx-auto max-w-2xl space-y-4 rounded-xl border border-border bg-white p-6 shadow-card">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
            Professional Findings Report
          </div>
          <div className="font-display text-2xl text-navy">{report.vendor} — Invoice Review</div>
          <div className="grid gap-3 sm:grid-cols-3">
            <MiniReportStat label="Invoice Total" value={currency(report.invoiceTotal)} />
            <MiniReportStat
              label="Fair Range"
              value={`${currency(report.fairRange[0])}–${currency(report.fairRange[1])}`}
            />
            <MiniReportStat
              label="Total Supported Recovery"
              value={`${currency(report.potentialSavings[0])}–${currency(report.potentialSavings[1])}`}
              tone="savings"
            />
          </div>
          <div className="border-t border-border/60 pt-3">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
              Executive Summary
            </div>
            <p className="mt-1.5 text-sm text-navy">
              This invoice contains {report.findings.length} questionable line items and{" "}
              {report.violations.length} potential statutory violations across storage,
              administrative, and environmental charges…
            </p>
          </div>
          <div className="border-t border-border/60 pt-3">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
              Findings
            </div>
            <ul className="mt-1.5 space-y-1.5 text-sm text-navy">
              {report.findings.slice(0, 3).map((f) => (
                <li key={f.item} className="flex justify-between gap-2">
                  <span>{f.item}</span>
                  <span className="font-mono text-savings">
                    {currency(f.savings[0])}–{currency(f.savings[1])}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <div className="flex justify-end">
        <Link
          to="/report/$id"
          params={{ id }}
          className="inline-flex items-center gap-1.5 rounded-md bg-navy px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-navy/90"
        >
          Open Full Report
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );
}

function MiniReportStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "savings";
}) {
  return (
    <div className="rounded-lg border border-border bg-secondary/40 p-3">
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </div>
      <div
        className={`mt-1 font-display text-lg ${tone === "savings" ? "text-savings" : "text-navy"}`}
      >
        {value}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Panel 9 — Activity timeline                                 */
/* ─────────────────────────────────────────────────────────── */

function ActivityTimeline({ bundle }: { bundle: FullWorkspaceBundle }) {
  const placeholder: {
    title: string;
    meta: string;
    time: string;
    icon: ReactNode;
    tone: Tone;
  }[] = [
    {
      title: "Draft letter saved",
      meta: "Thomas W.",
      time: "12m ago",
      icon: <PenLine className="h-3.5 w-3.5" />,
      tone: "negotiate",
    },
    {
      title: "AI findings completed",
      meta: "System",
      time: "1h ago",
      icon: <Sparkles className="h-3.5 w-3.5" />,
      tone: "trust",
    },
    {
      title: "Customer contacted",
      meta: "Thomas W. → Rachel K.",
      time: "1h ago",
      icon: <Mail className="h-3.5 w-3.5" />,
      tone: "navy",
    },
    {
      title: "Vendor contacted",
      meta: "Thomas W. → SafeMove",
      time: "3h ago",
      icon: <Phone className="h-3.5 w-3.5" />,
      tone: "navy",
    },
    {
      title: "Employee opened case",
      meta: "Thomas W.",
      time: "3h ago",
      icon: <User className="h-3.5 w-3.5" />,
      tone: "navy",
    },
    {
      title: "AI review started",
      meta: "System",
      time: "3h ago",
      icon: <Activity className="h-3.5 w-3.5" />,
      tone: "trust",
    },
    {
      title: "Invoice received",
      meta: "Rachel Kim",
      time: "Yesterday",
      icon: <Upload className="h-3.5 w-3.5" />,
      tone: "navy",
    },
  ];
  const realItems = bundle.timeline
    .slice()
    .reverse()
    .map((t) => ({
      title: t.title,
      meta: t.event_type,
      time: relativeTimeShort(t.created_at),
      icon: <Activity className="h-3.5 w-3.5" />,
      tone: "trust" as Tone,
    }));
  const items = realItems.length ? realItems : placeholder;
  return (
    <ol className="space-y-3">
      {items.map((it, i) => (
        <li
          key={i}
          className="flex items-start gap-3 rounded-lg border border-border bg-white px-3 py-2.5"
        >
          <div
            className={`flex h-8 w-8 items-center justify-center rounded-lg ${toneChip(it.tone)}`}
          >
            {it.icon}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-navy">{it.title}</div>
            <div className="text-xs text-muted-foreground">{it.meta}</div>
          </div>
          <div className="whitespace-nowrap text-[11px] text-muted-foreground">{it.time}</div>
        </li>
      ))}
    </ol>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Panel 10 — Internal checklist                               */
/* ─────────────────────────────────────────────────────────── */

function InternalChecklist({ bundle }: { bundle: FullWorkspaceBundle }) {
  const { user, isStaff, isAdmin } = useAuth();
  const canEdit = !!bundle.review && (isStaff || isAdmin);
  const [state, setState] = useState<ReviewChecklist>({});
  const [saving, setSaving] = useState(false);

  // Sync local checklist from the loaded review (real) or default demo state.
  useMemo(() => {
    if (bundle.review) {
      setState(bundle.review.checklist ?? {});
    } else {
      setState({
        invoice_verified: true,
        charges_reviewed: true,
        timeline_reviewed: true,
        laws_reviewed: true,
        zip_comparison_complete: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundle.review?.id]);

  const items = CHECKLIST_ITEMS.map((item) => ({
    ...item,
    done: !!state[item.key],
  }));
  const done = items.filter((i) => i.done).length;
  const pct = Math.round((done / items.length) * 100);

  async function toggle(key: keyof ReviewChecklist) {
    const next: ReviewChecklist = { ...state, [key]: !state[key] };
    setState(next);
    if (!bundle.review || !canEdit) return;
    setSaving(true);
    try {
      await saveChecklist(bundle.review.id, next);
    } catch (e) {
      toast.error("Checklist save failed", { description: (e as Error).message });
      setState(state); // revert
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-baseline justify-between">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
            Review Readiness{" "}
            {saving && <span className="ml-1 text-muted-foreground/70">Saving…</span>}
          </div>
          <div className="font-display text-lg text-navy">{pct}%</div>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-secondary">
          <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <ul className="space-y-1.5">
        {items.map((it) => (
          <li key={it.key}>
            <button
              type="button"
              onClick={() => toggle(it.key)}
              disabled={!bundle.review ? false : !canEdit}
              className="flex w-full items-center gap-3 rounded-md border border-border bg-white px-3 py-2 text-left text-sm transition hover:bg-secondary/40 disabled:cursor-not-allowed disabled:opacity-70"
            >
              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
                  it.done
                    ? "border-emerald-500 bg-emerald-500 text-white"
                    : "border-border bg-white text-transparent"
                }`}
              >
                <CheckCircle2 className="h-3 w-3" />
              </span>
              <span className={it.done ? "text-muted-foreground line-through" : "text-navy"}>
                {it.label}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {!canEdit && bundle.review ? (
        <div className="rounded-md border border-dashed border-border bg-secondary/40 px-3 py-2 text-[11px] text-muted-foreground">
          Read-only — sign in as staff to edit.
        </div>
      ) : null}
      {/* keep import referenced */}
      {user ? null : null}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Internal notes (staff/admin only, list + add)               */
/* ─────────────────────────────────────────────────────────── */

function InternalNotesSection({ bundle }: { bundle: FullWorkspaceBundle }) {
  const { user, isStaff, isAdmin } = useAuth();
  const canPost = !!bundle.review && (isStaff || isAdmin);
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);

  async function onAdd() {
    if (!bundle.review) return;
    const trimmed = body.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      await addInternalNote(bundle.review.id, trimmed, user?.id ?? null);
      setBody("");
      bundle.refresh();
      toast.success("Internal note added");
    } catch (e) {
      toast.error("Could not add note", { description: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
        <PenLine className="h-3.5 w-3.5" />
        Internal Notes
        {!bundle.review ? (
          <span className="ml-1 rounded bg-secondary px-1.5 py-0.5 text-[9px] font-semibold text-muted-foreground">
            Demo
          </span>
        ) : null}
      </div>
      {bundle.notes.length > 0 ? (
        <ul className="mb-2 space-y-1.5">
          {bundle.notes.map((n) => (
            <li
              key={n.id}
              className="rounded-md border border-border bg-white px-3 py-2 text-xs text-navy"
            >
              <div className="whitespace-pre-wrap">{n.body}</div>
              <div className="mt-1 text-[10px] text-muted-foreground">
                {n.author_id ? `#${n.author_id.slice(0, 6)}` : "Unknown"} ·{" "}
                {relativeTimeShort(n.created_at)}
              </div>
            </li>
          ))}
        </ul>
      ) : bundle.review ? (
        <div className="mb-2 rounded-md border border-dashed border-border bg-secondary/40 px-3 py-2 text-[11px] text-muted-foreground">
          No internal notes yet.
        </div>
      ) : null}
      {canPost ? (
        <div className="space-y-2">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Add internal note (visible to team only)…"
            rows={3}
            disabled={saving}
            className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-navy placeholder:text-muted-foreground focus:border-navy/30 focus:outline-none disabled:opacity-70"
          />
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onAdd}
              disabled={saving || !body.trim()}
              className="inline-flex items-center gap-1.5 rounded-md bg-navy px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-navy/90 disabled:opacity-60"
            >
              <Send className="h-3.5 w-3.5" />
              {saving ? "Adding…" : "Add Note"}
            </button>
          </div>
        </div>
      ) : bundle.review ? (
        <div className="rounded-md border border-dashed border-border bg-secondary/40 px-3 py-2 text-[11px] text-muted-foreground">
          Sign in as staff to add internal notes.
        </div>
      ) : (
        <NotesEditor
          label=""
          icon={<PenLine className="h-3.5 w-3.5" />}
          placeholder="Add internal review notes (only visible to team)…"
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Right sidebar                                               */
/* ─────────────────────────────────────────────────────────── */

function QuickActionsSidebar({ reviewId }: { reviewId: string }) {
  const { isAdmin } = useAuth();
  const pdf = useReportPdfActions(reviewId);

  type QA = {
    label: string;
    icon: ReactNode;
    tone?: Tone;
    onClick?: () => void;
    busy?: boolean;
  };

  const groups: { title: string; items: QA[] }[] = [
    {
      title: "Communication",
      items: [
        { label: "Email Customer", icon: <Mail className="h-4 w-4" /> },
        { label: "Call Customer", icon: <PhoneCall className="h-4 w-4" /> },
      ],
    },
    {
      title: "Case Management",
      items: [
        { label: "Assign Specialist", icon: <UserPlus className="h-4 w-4" /> },
        { label: "Change Status", icon: <Activity className="h-4 w-4" /> },
      ],
    },
    {
      title: "Documents",
      items: [
        {
          label: "Generate Report",
          icon: <FileText className="h-4 w-4" />,
          tone: "trust",
          onClick: pdf.downloadOrGenerate,
          busy: pdf.busy,
        },
        {
          label: "Generate Letter",
          icon: <PenLine className="h-4 w-4" />,
          tone: "negotiate",
        },
        {
          label: "Download PDF",
          icon: <Download className="h-4 w-4" />,
          onClick: pdf.downloadOrGenerate,
          busy: pdf.busy,
        },
      ],
    },
    {
      title: "Administration",
      items: [
        { label: "Archive Review", icon: <ArchiveRestore className="h-4 w-4" /> },
        ...(isAdmin
          ? [
              {
                label: "Delete",
                icon: <AlertTriangle className="h-4 w-4" />,
                tone: "amber" as Tone,
              },
            ]
          : []),
      ],
    },
  ];

  return (
    <div className="space-y-4 rounded-2xl border border-border bg-card p-4 shadow-card">
      <div className="flex items-center justify-between px-1">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
          Quick Actions
        </div>
      </div>
      <div className="space-y-4">
        {groups.map((g) => (
          <div key={g.title}>
            <div className="px-1 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80">
              {g.title}
            </div>
            <ul className="space-y-1 rounded-xl border border-border bg-secondary/20 p-1">
              {g.items.map((a) => (
                <li key={a.label}>
                  <button
                    type="button"
                    disabled={a.busy}
                    onClick={
                      a.onClick ??
                      (() =>
                        toast(a.label, {
                          description: "Placeholder — API integration pending.",
                        }))
                    }
                    className="group flex w-full items-center gap-2.5 rounded-lg border border-transparent bg-card px-2.5 py-2 text-left text-[13px] font-medium text-navy shadow-sm transition hover:border-border hover:bg-secondary/60 disabled:opacity-60"
                  >
                    <span
                      className={`flex h-7 w-7 items-center justify-center rounded-md ${toneChip(a.tone ?? "navy")}`}
                    >
                      {a.icon}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{a.label}</span>
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-navy" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Helpers for real-data integration ─────────────────────

function prettyStatus(s: string): string {
  switch (s) {
    case "received":
      return "Received";
    case "reviewing":
      return "In Review";
    case "report_ready":
      return "Report Ready";
    case "self_serve":
      return "Ready to Negotiate";
    case "negotiating":
      return "Negotiating";
    case "waiting_vendor":
      return "Waiting on Vendor";
    case "offer_received":
      return "Offer Received";
    case "completed":
      return "Completed";
    default:
      return s;
  }
}

function relativeTimeShort(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function WorkspaceSkeleton() {
  return (
    <div className="min-h-dvh bg-secondary/30">
      <SiteHeader />
      <div className="container-page py-14">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-1/3 rounded bg-white/70" />
          <div className="h-4 w-1/2 rounded bg-white/70" />
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="space-y-4">
              <div className="h-64 rounded-2xl bg-white/70" />
              <div className="h-40 rounded-2xl bg-white/70" />
              <div className="h-40 rounded-2xl bg-white/70" />
            </div>
            <div className="h-72 rounded-2xl bg-white/70" />
          </div>
        </div>
      </div>
    </div>
  );
}

// Silence unused-import warnings for models referenced only via bundle
void (null as unknown as TimelineEvent);
void (null as unknown as InternalNote);
void (null as unknown as ReviewFile);
